/**
 * LE TERRITOIRE D'UN ATELIER, CÔTÉ AUTHORING.
 *
 * Un atelier qui déclare un territoire fait charger sa carte 3D pour toute la
 * séance : elle sert au module d'intro, puis de fond aux pages qui le
 * demandent. Deux champs, et le second n'est PAS saisi à la main.
 *
 * POURQUOI LES COUCHES SONT CALCULÉES. `layers` est ce que la scène CONSTRUIT ;
 * le changer reconstruit tout (cf. terrain3d/TerritoryStage.js), donc ça se
 * décide une fois pour la séance. Or une couche qu'une page allume sans qu'elle
 * ait été construite ne produit ni erreur ni message : elle est simplement
 * absente. Une case à cocher oubliée ne se voit donc qu'en salle — et c'est
 * exactement pour ça qu'on ne demande pas à l'auteur de cocher. La liste se
 * déduit de ce que les pages de la pile utilisent réellement.
 */

/** Couches qu'une page a besoin de voir construites. */
export function layersUsedByPage(page) {
  if (!page) return [];
  // Module d'intro : ses étapes balaient les couches, et `content.layers` en
  // porte déjà l'union — c'est ce champ qui pilotait le viewer avant que la
  // carte ne devienne un objet de séance.
  if (page.type === "territory_intro") {
    const declared = page.content?.layers || [];
    const perStep = (page.content?.steps || []).flatMap((s) => s.active_layers || []);
    return [...declared, ...perStep];
  }
  // Toute autre page : ce que sa vue de carte allume.
  const view = page.map_view;
  if (!view || !view.mode || view.mode === "off") return [];
  return view.active_layers || [];
}

/**
 * Union des couches utilisées par la pile, dans l'ordre alphabétique (le même
 * que celui dont l'écran se sert pour comparer deux ensembles).
 *
 * Les pages introuvables sont ignorées en silence : une pile qui référence une
 * page supprimée est un problème, mais ce n'est pas celui-ci, et la validation
 * de l'atelier le signale déjà.
 */
export function computeTerritoryLayers(pile, pagesById) {
  const used = new Set();
  (pile || []).forEach((pageId) => {
    layersUsedByPage(pagesById[pageId]).forEach((layer) => used.add(layer));
  });
  return [...used].sort();
}

/**
 * SUR QUEL TERRITOIRE UNE PAGE EST-ELLE RÉGLÉE ? Déduit, jamais choisi.
 *
 * Un cadrage et un repère sont exprimés en coordonnées du terrain : ils ne
 * valent que pour un territoire. Or ce n'est pas la page qui le décide, ce sont
 * les ateliers qui l'emploient — et un atelier n'a qu'un territoire, figé. La
 * réponse est donc calculable, et laisser l'auteur la choisir dans une liste
 * n'ouvrirait qu'une occasion de se tromper.
 *
 * Trois issues, et l'appelant les traite différemment :
 *   - `territoryId` : le territoire imposé, à afficher sans permettre d'en changer ;
 *   - `free: true`  : la page n'est encore dans aucun atelier porteur de carte —
 *                     il faut bien un décor pour régler, donc on laisse choisir ;
 *   - `conflicting` : deux ateliers de territoires différents l'emploient, ce
 *                     qui ne peut pas donner un repère valable des deux côtés.
 */
export function territoryOfPage(pageId, configs) {
  const owners = (configs || []).filter((c) => (c.pile || []).includes(pageId));
  const withTerritory = owners.filter((c) => c.territory?.id);
  const withoutTerritory = owners.filter((c) => !c.territory?.id);
  const ids = [...new Set(withTerritory.map((c) => c.territory.id))];

  // PEUT-ELLE ÊTRE LOCALISÉE ? Poser un repère, c'est promettre un lieu sur un
  // terrain précis. La promesse ne tient que si TOUS les ateliers qui emploient
  // la page se déroulent sur ce même terrain — un seul atelier sans carte, ou
  // sur un autre territoire, et le repère y tombe dans le vide.
  //
  // Une page qui n'est encore dans aucun atelier reste libre : rien ne s'oppose
  // à ce qu'elle soit localisée, et c'est à l'ajout dans une pile que la
  // compatibilité sera vérifiée.
  let canLocate = true;
  let blockReason = null;
  if (ids.length > 1) {
    canLocate = false;
    blockReason = `Cette page est partagée par des ateliers sur des territoires différents (${ids.join(", ")}) : un repère ne peut pas valoir pour les deux.`;
  } else if (withoutTerritory.length > 0) {
    canLocate = false;
    blockReason = `Cette page est utilisée par ${withoutTerritory.length > 1 ? "des ateliers sans carte" : "un atelier sans carte"} (${withoutTerritory.map((c) => c._id).join(", ")}) : son repère n'y existerait nulle part.`;
  }

  return {
    territoryId: ids[0] || null,
    ids,
    // Le territoire de composition est un CHOIX tant qu'aucun atelier ne
    // l'impose. Distinct de `canLocate` : une page sans aucun propriétaire est
    // à la fois libre et localisable.
    free: ids.length === 0,
    conflicting: ids.length > 1,
    canLocate,
    blockReason,
    // Ateliers nommés, pour pouvoir dire lesquels posent problème.
    owners: withTerritory.map((c) => ({ id: c._id, territory: c.territory.id })),
    ownersWithoutTerritory: withoutTerritory.map((c) => c._id),
  };
}

/** Territoire sur lequel une page est localisée, `null` si elle ne l'est pas. */
export function locatedOn(page) {
  const view = page?.map_view;
  return view?.mode === "located" ? (view.territory_id || null) : null;
}

/**
 * Une page peut-elle entrer dans la pile de cet atelier ?
 *
 * Seules les pages LOCALISÉES sont refusées : un fond de carte dans un atelier
 * sans territoire est simplement inerte, alors qu'un repère y désigne un lieu
 * qui n'existe pas. Rend `{ ok }` ou `{ ok: false, reason }`.
 */
export function pageFitsConfig(page, configTerritoryId) {
  const on = locatedOn(page);
  if (!on) return { ok: true };
  if (!configTerritoryId) {
    return {
      ok: false,
      reason: `Page localisée sur « ${on} » : cet atelier n'a pas de territoire.`,
    };
  }
  if (on !== configTerritoryId) {
    return {
      ok: false,
      reason: `Page localisée sur « ${on} », cet atelier se déroule sur « ${configTerritoryId} ».`,
    };
  }
  return { ok: true };
}

/** Pages de la pile qui réclament une carte, avec ce qu'elles en font. */
export function territoryUsers(pile, pagesById) {
  const seen = new Set();
  const users = [];
  (pile || []).forEach((pageId) => {
    if (seen.has(pageId)) return;
    const page = pagesById[pageId];
    const layers = layersUsedByPage(page);
    if (!page || layers.length === 0) return;
    seen.add(pageId);
    users.push({
      pageId,
      title: page.title || pageId,
      usage: page.type === "territory_intro" ? "module d'intro" : `fond · ${page.map_view.mode}`,
      // Dédoublonnées : un module d'intro déclare ses couches ET les rallume
      // étape par étape, ce qui répétait « rivers » cinq fois dans le tableau.
      layers: [...new Set(layers)].sort(),
    });
  });
  return users;
}
